import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Skeleton, notification } from 'antd';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';
import moment from 'moment-timezone';
import APIClient from '../api/api';
import { LanderDetail as LanderDetailResponse } from '../api/models';
import { Callout, ChartTooltipContent, DashboardCard, axisTickStyle, chartColors, gridStroke, legendStyle } from '../components/ui';
import { DateRangePopover, useDateRangeFilter } from '../components/DateRangeFilter';
import { LanderBreakdownCard } from '../components/LanderBreakdownCard';
import GrowthStatistic from '../components/GrowthStatistic';
import { useSlowRequest } from '../hooks/useSlowRequest';
import { PageBody, PageHead } from '../layout';

const LanderDetail = () => {
    const { domain } = useParams<{ domain: string }>();
    const { dateRange, setDateRange, selectedQuickRange, setSelectedQuickRange, previousPeriodRange } = useDateRangeFilter('this-month');

    const [detail, setDetail] = useState<LanderDetailResponse>();
    const [previous, setPrevious] = useState<LanderDetailResponse>();
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    // A lander among the busiest of every day in the range is answered from
    // the stored tables in milliseconds. Anything slower is being read out of
    // Plausible one lander at a time, which takes around ten seconds.
    const readingLive = useSlowRequest(loading);

    useEffect(() => {
        loadLander();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domain, dateRange]);

    async function loadLander() {
        if (!domain) return;
        setLoading(true);
        setFailed(false);
        try {
            const [current, prior] = await Promise.all([
                APIClient.getLander(domain, dateRange[0].toDate(), dateRange[1].toDate()),
                previousPeriodRange
                    ? APIClient.getLander(domain, previousPeriodRange[0].toDate(), previousPeriodRange[1].toDate())
                    : Promise.resolve(undefined),
            ]);
            setDetail(current);
            setPrevious(prior);
        } catch (e) {
            setFailed(true);
            setDetail(undefined);
            setPrevious(undefined);
            notification.error({ message: 'Failed to load lander traffic.' });
        } finally {
            setLoading(false);
        }
    }

    const chartData = detail?.timeseries.map((point) => ({
        name: moment(point.date).tz('UTC').format('M/D'),
        Uniques: point.visitors,
        Pageviews: point.pageviews,
    }));

    const breakdowns = detail?.breakdowns;

    return (
        <>
            <PageHead
                title={domain}
                subtitle={<Link to="/landers" className="text-accent hover:opacity-80">← All landers</Link>}
                actions={
                    <DateRangePopover
                        dateRange={dateRange}
                        setDateRange={setDateRange}
                        selectedQuickRange={selectedQuickRange}
                        setSelectedQuickRange={setSelectedQuickRange}
                        loading={loading}
                    />
                }
            />
            <PageBody>
                {failed ? (
                    <Callout tone="danger" title="Traffic unavailable">
                        No traffic could be loaded for this lander.
                    </Callout>
                ) : (
                    <>
                    {readingLive && (
                        <div className="mb-2.5">
                            <Callout tone="info" title="Reading this lander's traffic live">
                                Only the busiest landers are stored each day, and {domain} is not among them
                                for this period. Its traffic is being read from Plausible directly, which takes
                                around ten seconds.
                            </Callout>
                        </div>
                    )}
                    <Skeleton loading={loading && !detail}>
                        <div className="flex flex-col gap-2.5">
                            <DashboardCard title="Traffic">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    <GrowthStatistic previousTitle="Previous" title="Uniques" percentage currentValue={detail?.totals.visitors} previousValue={previous?.totals.visitors} />
                                    <GrowthStatistic previousTitle="Previous" title="Visits" percentage currentValue={detail?.totals.visits} previousValue={previous?.totals.visits} />
                                    <GrowthStatistic previousTitle="Previous" title="Pageviews" percentage currentValue={detail?.totals.pageviews} previousValue={previous?.totals.pageviews} />
                                    <GrowthStatistic previousTitle="Previous" title="Events" percentage currentValue={detail?.totals.events} previousValue={previous?.totals.events} />
                                </div>
                            </DashboardCard>

                            <DashboardCard title="Uniques and Pageviews">
                                {chartData && chartData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={280}>
                                        <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                                            <defs>
                                                {['Uniques', 'Pageviews'].map((_, x) => (
                                                    <linearGradient key={x} id={`lander-traffic-${x}`} x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor={chartColors[x % chartColors.length]} stopOpacity={0.15} />
                                                        <stop offset="100%" stopColor={chartColors[x % chartColors.length]} stopOpacity={0} />
                                                    </linearGradient>
                                                ))}
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                                            <XAxis dataKey="name" tick={axisTickStyle} axisLine={false} tickLine={false} />
                                            <YAxis tick={axisTickStyle} axisLine={false} tickLine={false} width={50} />
                                            <RechartsTooltip content={<ChartTooltipContent />} />
                                            <Legend iconType="circle" iconSize={8} wrapperStyle={legendStyle} />
                                            {['Uniques', 'Pageviews'].map((key, x) => (
                                                <Area key={key} type="monotone" dataKey={key} stroke={chartColors[x % chartColors.length]} fill={`url(#lander-traffic-${x})`} strokeWidth={2} dot={false} activeDot={{ r: 3, strokeWidth: 0 }} />
                                            ))}
                                        </AreaChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <span className="text-text-3 text-sm">No traffic in this period.</span>
                                )}
                            </DashboardCard>

                            <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-2.5">
                                <LanderBreakdownCard title="Sources" entries={breakdowns?.source ?? []} loading={loading} />
                                <LanderBreakdownCard title="Countries" entries={breakdowns?.country ?? []} loading={loading} />
                                <LanderBreakdownCard title="Devices" entries={breakdowns?.device ?? []} loading={loading} />
                                <LanderBreakdownCard title="Browsers" entries={breakdowns?.browser ?? []} loading={loading} />
                                <LanderBreakdownCard title="Operating Systems" entries={breakdowns?.os ?? []} loading={loading} />
                                <LanderBreakdownCard title="Pages" entries={breakdowns?.page ?? []} loading={loading} />
                            </div>
                        </div>
                    </Skeleton>
                    </>
                )}
            </PageBody>
        </>
    );
};

export default LanderDetail;
